import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';
import { selectAuthToken } from '../store/auth/selectors/auth.selectors';
import { JwtHelperService } from './jwt-helper.service';

/**
 * Admin Guard
 *
 * Protects routes that require ADMIN or SUPER_ADMIN role.
 * Checks the user's roles decoded from the JWT token.
 *
 * Usage in routes:
 * ```typescript
 * {
 *   path: 'admin',
 *   canActivate: [adminGuard],
 *   component: AdminComponent
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class AdminGuardService {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly jwtHelper = inject(JwtHelperService);

  canActivate(): Observable<boolean> {
    return this.store.select(selectAuthToken).pipe(
      map((token) => {
        if (!token?.accessToken) {
          console.warn('AdminGuard: No access token found, redirecting to login');
          void this.router.navigate(['/login']);
          return false;
        }

        // Check if user has ADMIN or SUPER_ADMIN role
        const hasAdminRole = this.jwtHelper.hasAnyRole(token.accessToken, [
          'ROLE_ADMIN',
          'ROLE_SUPER_ADMIN',
        ]);

        if (!hasAdminRole) {
          console.warn('AdminGuard: User does not have admin role, redirecting to dashboard');
          void this.router.navigate(['/dashboard']);
          return false;
        }

        return true;
      }),
    );
  }
}

/**
 * Functional guard for use in route configuration
 */
export const adminGuard = () => {
  return inject(AdminGuardService).canActivate();
};
